import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { paths } from '../../../common/constants/pathVariables.js';
import { parseBasketJson, useShoppingBasket } from '../../../common/hooks/useShoppingBasket.js';
import { useVoucher } from '../../../common/hooks/useVoucher';
import { ScrollToTop } from '../../../common/utils/browserUtils';
import { GridNarrow } from '../../../components/Grid/Grid.js';
import { getUserAccounts } from '../../../components/Header/dynamic/headerFunctions.js';
import { LoginAndRegistration } from '../../../components/LoginAndRegistration/LoginAndRegistration.js';
import { useModelsForBasket } from '../../../components/PlaceOrder/hooks/useModelsForBasket';
import { handleAddOrRemoveItemForPlaceOrder } from '../../../components/PlaceOrder/utils/placeOrderAnalyticsUtils';
import { LoadingShoppingBasket } from '../../../components/ShoppingBasket/LoadingShoppingBasket';
import { ShoppingBasket } from '../../../components/ShoppingBasket/ShoppingBasket.js';
import {
  changePaymentOption,
  changeQuantityInBasket,
  findBasketItemForId,
  setActiveVouchersInBasket,
} from '../../../components/ShoppingBasket/shoppingBasketUtils.js';
import { useAuth } from '../AuthProvider.js';
import { useSessionData } from '../SessionDataProvider.js';
import { useOnlineModelsContext } from '../onlineModel/onlineModelContextProvider';
import { getOnlineModelsWithVoucher } from '../onlineModel/onlineModelService';
import type { ShoppingBasketType } from '../../../common/types/shoppingBasket.js';
import type { ChangePaymentOptions } from '../../../components/ShoppingBasket/types';

export const ShoppingBasketPath = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const loginInProgress = location.state?.loginInProgress === true;
  const { isAuthenticated, authenticatedUser, inMigrationPhase } = useAuth();

  const { shoppingBasket, setShoppingBasket } = useShoppingBasket();
  const shoppingCartJson: ShoppingBasketType = parseBasketJson(shoppingBasket);
  const { priceAlteredModels, fetchModelsByCodes, setActiveVoucher, activeVoucherCode, loadVoucherModels } =
    useOnlineModelsContext();
  const baseModels = useModelsForBasket(shoppingCartJson);
  const onlineModelCodeGuids = [...new Set(shoppingCartJson.items?.map(cartItem => cartItem.guid))];
  const basketVoucherCode = shoppingCartJson.vouchers?.[0];

  const companyInfo = useSessionData().companyInfo;

  useEffect(() => {
    if (basketVoucherCode !== activeVoucherCode) {
      setActiveVoucher(basketVoucherCode ?? '');
    }
  }, [basketVoucherCode, activeVoucherCode, setActiveVoucher]);

  const voucherOnlineModels = basketVoucherCode
    ? getOnlineModelsWithVoucher(onlineModelCodeGuids, basketVoucherCode, priceAlteredModels.onlineModelsByVoucher)
    : undefined;
  const previousVoucherRef = useRef<string | undefined>(undefined);

  const voucherHook = useVoucher(
    loadVoucherModels,
    setActiveVoucher,
    fetchModelsByCodes,
    shoppingCartJson,
    basketVoucherCode,
    voucherOnlineModels
  );

  // Load voucher models when voucher code is present and models are available
  useEffect(() => {
    const shouldLoad =
      isAuthenticated && basketVoucherCode && baseModels.length > 0 && previousVoucherRef.current !== basketVoucherCode;
    if (shouldLoad) {
      previousVoucherRef.current = basketVoucherCode;
      voucherHook.applyVoucher(basketVoucherCode);
    }
    if (!basketVoucherCode) {
      previousVoucherRef.current = undefined;
    }
  }, [isAuthenticated, basketVoucherCode, baseModels.length, voucherHook, setShoppingBasket, shoppingCartJson]);

  // TODO BULK-API: Remove when private/bulk API returns correct campaign data.
  const voucherModels = voucherOnlineModels?.length ? voucherOnlineModels : voucherHook.updatedModels;
  const onlineModels = voucherHook.selectedVoucherCode ? voucherModels : baseModels;
  // Use base online models as fallback to retain products while voucher models are loading in another tab
  const displayModels = onlineModels.length > 0 ? onlineModels : baseModels;

  const changeQuantity = (cartItemId: string, quantity: number) => {
    const basketItem = findBasketItemForId(shoppingCartJson.items, cartItemId);
    handleAddOrRemoveItemForPlaceOrder(basketItem, quantity, displayModels);
    setShoppingBasket(JSON.stringify(changeQuantityInBasket(shoppingCartJson, cartItemId, quantity)));
    if (shoppingCartJson?.items?.length === 0) {
      setActiveVoucher('');
      setShoppingBasket(JSON.stringify(setActiveVouchersInBasket(shoppingCartJson, [])));
    }
  };

  const onPaymentOptionChange = (changePaymentOptions: ChangePaymentOptions) => {
    setShoppingBasket(JSON.stringify(changePaymentOption(changePaymentOptions)));
  };

  const userAccounts = getUserAccounts(authenticatedUser) || [];

  const isFetching = authenticatedUser?.isFetching;
  const isCompanyNameEmpty = companyInfo?.companyName && companyInfo?.companyName?.length === 0;
  /* Checkout page initiates a bug situation:
    When user arrives back here from Checkout the companyInfo?.companyInfoLoading is left to true.
    This is because it dispatches LOAD_COMPANY_INFO but never gets _FULFILLED or _FAILED state.
    Therefore, use company name to determine when page is loading.
   */
  const somePublicModelsLoaded =
    onlineModelCodeGuids.length === 0 ||
    onlineModelCodeGuids.some(guid => baseModels.find(model => model.onlineModelCode === guid));
  const someDiscountsLoaded =
    onlineModelCodeGuids.length === 0 ||
    onlineModelCodeGuids.some(guid => baseModels.find(model => model.onlineModelCode === guid));
  const isLoadingPrivateOnlineModels = isAuthenticated && !someDiscountsLoaded && !activeVoucherCode;
  const isLoadingPublicOnlineModels = !isAuthenticated && !somePublicModelsLoaded;
  const isLoading =
    isFetching ||
    (isFetching === false && isCompanyNameEmpty === undefined) ||
    isLoadingPublicOnlineModels ||
    isLoadingPrivateOnlineModels;

  useEffect(() => {
    if (!isLoading && loginInProgress && userAccounts.length === 1) {
      // If there is no need for user to select company first: reset state
      navigate(paths.SHOPPING_BASKET, { replace: true });
    }
  }, [loginInProgress, navigate, userAccounts.length, isLoading]);

  const updateBasket = (basket: string) => {
    setShoppingBasket(basket);
  };

  if (!isAuthenticated && loginInProgress) {
    if (inMigrationPhase) {
      return (
        <>
          <ScrollToTop />
          <LoginAndRegistration includeAdmins />
        </>
      );
    }
    return (
      <GridNarrow>
        <ScrollToTop />
        <LoginAndRegistration />
      </GridNarrow>
    );
  }

  if (isLoading) {
    return (
      <div>
        <LoadingShoppingBasket basketItems={shoppingCartJson.items} />
      </div>
    );
  }

  return (
    <div>
      <ShoppingBasket
        userAccounts={userAccounts}
        shoppingBasket={shoppingCartJson}
        models={displayModels}
        loggedIn={isAuthenticated}
        loginInProgress={loginInProgress}
        changeQuantity={changeQuantity}
        onPaymentOptionChange={onPaymentOptionChange}
        eppSolution={companyInfo?.eppSolution}
        voucherHook={voucherHook}
        updateBasket={updateBasket}
      />
    </div>
  );
};
